import Dialog from "@mui/material/Dialog";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import React, { useState } from "react";
import useStore from "store/hook";
import CloseIcon from "@mui/icons-material/Close";
import LocationCityOutlinedIcon from "@mui/icons-material/LocationCityOutlined";
import { Checkbox } from "@mui/material";
import SoftAvatar from "components/SoftAvatar";
import defaultUniversity from "assets/images/university/defaultuniversity.png";
import { useGetschoolistQuery } from "store/api/universities.api";
import { useGetCityListQuery } from "store/api/cities.api";
import { FadeLoader } from "react-spinners";
import emptyUniversity from "assets/images/university/emptyUniversity.png";
import SoftButton from "components/SoftButton";
import Formatmessage from "services/Formatmessage";
import { toast } from "react-toastify";
import { showDeletedCheckboxSx } from "constants/listFilters";

const UniversityList = ({
  selectedUniversitiesData,
  selectedUniversitiesIdsData,
  selectedUniversities,
  setSelectedUniversities,
  selectedUniversitiesIds,
  setSelectedUniversitiesIds,
  selectedCitiesData,
  selectedCitiesIdsData,
  selectedCities,
  setSelectedCities,
  selectedCitiesIds,
  setSelectedCitiesIds,
  onApply,
}) => {
  const { store, setDialogue } = useStore();
  const [activeTab, setActiveTab] = useState("schools");
  const [schoolsData, setSchoolsData] = useState([]);
  const [citiesData, setCitiesData] = useState([]);

  const { data: schoolsResponse, isLoading: schoolsLoading, isFetching: schoolsFetching } =
    useGetschoolistQuery({
      page: -1,
      type: "list",
    });

  const { data: citiesResponse, isLoading: citiesLoading, isFetching: citiesFetching } =
    useGetCityListQuery({
      page: -1,
      type: "list",
    });

  React.useEffect(() => {
    if (schoolsResponse?.status === 1) {
      const schools = schoolsResponse?.data?.schools || schoolsResponse?.data || [];
      setSchoolsData(Array.isArray(schools) ? schools : []);
    } else if (schoolsResponse?.status == 0) {
      toast.error(Formatmessage(schoolsResponse?.message));
    }
  }, [schoolsResponse]);

  React.useEffect(() => {
    if (citiesResponse?.status === 1) {
      const cities = citiesResponse?.data?.cities || citiesResponse?.data || [];
      setCitiesData(Array.isArray(cities) ? cities : []);
    } else if (citiesResponse?.status == 0) {
      toast.error(Formatmessage(citiesResponse?.message));
    }
  }, [citiesResponse]);

  const handleSelectSchool = (school) => {
    setSelectedUniversities((prev) => {
      const exists = prev.some((item) => item?._id === school?._id);
      return exists ? prev.filter((item) => item?._id !== school?._id) : [...prev, school];
    });

    setSelectedUniversitiesIds((prev) => {
      const exists = prev.includes(school?._id);
      return exists ? prev.filter((item) => item !== school?._id) : [...prev, school?._id];
    });
  };

  const handleSelectCity = (city) => {
    setSelectedCities((prev) => {
      const exists = prev.some((item) => item?._id === city?._id);
      return exists ? prev.filter((item) => item?._id !== city?._id) : [...prev, city];
    });

    setSelectedCitiesIds((prev) => {
      const exists = prev.includes(city?._id);
      return exists ? prev.filter((item) => item !== city?._id) : [...prev, city?._id];
    });
  };

  const handleApply = () => {
    onApply(selectedUniversities, selectedUniversitiesIds, selectedCities, selectedCitiesIds);
    setDialogue({
      type: "openUniversitylist",
      isOpen: false,
      action: "",
    });
  };

  const {
    app: {
      dialogs: {
        openUniversitylist: { isOpen },
      },
    },
  } = store;

  React.useEffect(() => {
    if (isOpen) {
      setActiveTab("schools");
      setSelectedUniversities(selectedUniversitiesData);
      setSelectedUniversitiesIds(selectedUniversitiesIdsData);
      setSelectedCities(selectedCitiesData);
      setSelectedCitiesIds(selectedCitiesIdsData);
    }
  }, [isOpen]);

  const handleClose = () => {
    setSelectedUniversities(selectedUniversitiesData);
    setSelectedUniversitiesIds(selectedUniversitiesIdsData);
    setSelectedCities(selectedCitiesData);
    setSelectedCitiesIds(selectedCitiesIdsData);
    setDialogue({
      type: "openUniversitylist",
      isOpen: false,
      action: "",
    });
  };

  const isLoading =
    activeTab === "schools"
      ? schoolsLoading || schoolsFetching
      : citiesLoading || citiesFetching;

  const tabConfig = {
    schools: {
      title: "Schools",
      subtitle: "Choose one or more Schools from the list",
    },
    cities: {
      title: "Cities",
      subtitle: "Choose one or more Cities from the list",
    },
  };

  const renderSchoolsList = () =>
    schoolsData?.length > 0 ? (
      schoolsData.map((school) => (
        <SoftBox
          display="flex"
          width="100%"
          key={school?._id}
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
          }}
          onClick={() => handleSelectSchool(school)}
        >
          <SoftBox display="flex" alignItems="center" justifyContent="center" gap={2}>
            <SoftBox borderRadius="50%">
              <SoftAvatar
                src={school?.school_image || defaultUniversity}
                alt="school"
                size="sm"
              />
            </SoftBox>
            <SoftTypography fontWeight="normal" sx={{ fontSize: 17, color: "black.main" }}>
              {school?.school_name}
            </SoftTypography>
          </SoftBox>

          <SoftBox display="flex" alignItems="center" justifyContent="center">
            <Checkbox checked={selectedUniversitiesIds?.includes(school?._id)} sx={showDeletedCheckboxSx} />
          </SoftBox>
        </SoftBox>
      ))
    ) : (
      renderEmptyState("No Schools Available", "We couldn't find any schools in the system.")
    );

  const renderCitiesList = () =>
    citiesData?.length > 0 ? (
      citiesData.map((city) => (
        <SoftBox
          display="flex"
          width="100%"
          key={city?._id}
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
          }}
          onClick={() => handleSelectCity(city)}
        >
          <SoftBox display="flex" alignItems="center" justifyContent="center" gap={2}>
            <SoftBox
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                backgroundColor: "#E2E2E2",
              }}
            >
              <LocationCityOutlinedIcon sx={{ height: 22, width: 22, color: "#555F73" }} />
            </SoftBox>
            <SoftTypography
              fontWeight="normal"
              sx={{ fontSize: 17, color: "black.main", textTransform: "capitalize" }}
            >
              {city?.city}
            </SoftTypography>
          </SoftBox>

          <SoftBox display="flex" alignItems="center" justifyContent="center">
            <Checkbox checked={selectedCitiesIds?.includes(city?._id)} sx={showDeletedCheckboxSx} />
          </SoftBox>
        </SoftBox>
      ))
    ) : (
      renderEmptyState("No Cities Available", "We couldn't find any cities in the system.")
    );

  const renderEmptyState = (title, description) => (
    <SoftBox
      sx={{
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "start",
        alignItems: "center",
        gap: 2,
      }}
    >
      <SoftBox display="flex" flexDirection="column" gap={8} justifyContent="center" alignItems="center" width="100%">
        <SoftBox display="flex" flexDirection="column" alignItems="center">
          <SoftBox
            height="150px"
            width="150px"
            borderRadius="lg"
            sx={{
              backgroundImage: `url(${emptyUniversity})`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              backgroundSize: "contain",
            }}
          />
          <SoftTypography fontWeight="bold" color="black" sx={{ fontSize: 20 }}>
            {title}
          </SoftTypography>
          <SoftTypography fontWeight="medium" align="center" color="gray" sx={{ fontSize: 12 }}>
            {description}
          </SoftTypography>
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "25px",
          m: { xs: 1, sm: 1, md: 2 },
        },
      }}
    >
      <SoftBox
        sx={{ boxSizing: "border-box", p: 0.5, backgroundColor: "secondary.main" }}
        borderRadius="xxl"
        width="100%"
      />

      <SoftBox
        width="100%"
        display="flex"
        flexDirection="column"
        sx={{
          overflowY: "scroll",
          backgroundColor: "white.main",
          "&::-webkit-scrollbar": { display: "none" },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        <SoftBox display="flex" width="100%" flexDirection="column" gap={1} sx={{ p: { xs: 1, sm: 2, md: 4 } }}>
          <SoftBox
            display="flex"
            width="100%"
            sx={{ alignItems: "center", justifyContent: "space-between", textAlign: "start" }}
          >
            <SoftTypography fontWeight="medium" sx={{ fontSize: 20, color: "primary.main" }}>
              {tabConfig[activeTab].title}
            </SoftTypography>

            <SoftBox
              onClick={handleClose}
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{ cursor: "pointer" }}
              p={0.5}
            >
              <CloseIcon sx={{ color: "black.main" }} />
            </SoftBox>
          </SoftBox>

          <SoftBox display="flex" gap={1}>
            {["schools", "cities"].map((tab) => (
              <SoftBox
                key={tab}
                onClick={() => setActiveTab(tab)}
                sx={{
                  px: 2,
                  py: 0.75,
                  borderRadius: "8px",
                  cursor: "pointer",
                  backgroundColor: activeTab === tab ? "skyblue.main" : "transparent",
                  border: activeTab === tab ? "1px solid #3DC2EB" : "1px solid #D8E0E8",
                }}
              >
                <SoftTypography
                  sx={{
                    fontSize: 14,
                    fontWeight: activeTab === tab ? 600 : 400,
                    color: activeTab === tab ? "primary.main" : "black.main",
                    textTransform: "capitalize",
                  }}
                >
                  {tab}
                </SoftTypography>
              </SoftBox>
            ))}
          </SoftBox>

          <SoftBox display="flex" width="100%" sx={{ alignItems: "center", justifyContent: "space-between" }}>
            <SoftTypography fontWeight="normal" sx={{ fontSize: 20, color: "black.main" }}>
              {tabConfig[activeTab].subtitle}
            </SoftTypography>
          </SoftBox>

          <SoftBox
            sx={{
              maxHeight: "45vh",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              pr: 0.5,
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#D8E0E8",
                borderRadius: "8px",
              },
            }}
          >
            {isLoading ? (
              <SoftBox
                sx={{
                  height: "calc(30vh)",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <FadeLoader color="#3DC2EB" />
              </SoftBox>
            ) : activeTab === "schools" ? (
              renderSchoolsList()
            ) : (
              renderCitiesList()
            )}
          </SoftBox>

          <SoftBox width="100%" display="flex" alignItems="center" justifyContent="center">
            <SoftButton
              onClick={handleApply}
              color="primary"
              sx={{ textTransform: "capitalize", fontSize: 16, width: "200px" }}
              variant="contained"
            >
              Apply
            </SoftButton>
          </SoftBox>
        </SoftBox>
      </SoftBox>

      <SoftBox
        sx={{ boxSizing: "border-box", p: 0.5, backgroundColor: "secondary.main", mt: "auto" }}
        borderRadius="xxl"
        width="100%"
      />
    </Dialog>
  );
};

export default UniversityList;
